import { db, functions, httpsCallable } from '@util/firebase';
import {
  ReviewDocument,
  reviewDocumentSchema,
} from '@util/types/firestore/review';
import {
  collection,
  CollectionReference,
  query,
  where,
  limit,
  getDocs,
} from 'firebase/firestore';
import { getUserByUsername } from '../users';

const reviewsRef = collection(
  db,
  'reviews'
) as CollectionReference<ReviewDocument>;

export const getReviewByOrderId = async (id: string) => {
  const q = query(reviewsRef, where('order_id', '==', id), limit(1));
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }
  const data = reviewDocumentSchema.parse(snapshot.docs?.[0]?.data() ?? null);
  return data;
};

type RatingChoice = 'Bad' | 'Good' | 'Excellent';
interface SubmitReviewPayload {
  order_id: string;
  rating: number;
  comment?: string;
  communication?: RatingChoice;
  delivery_time?: RatingChoice;
  payment?: RatingChoice;
  reviewer_role: 'buyer' | 'seller';
  // CF requires this so it knows which specific party on the order is being
  // reviewed. Critical for multi-seller orders — without it the CF defaults
  // to seller_arr[0] and silently overwrites the wrong review.
  subject_uid: string;
  products?: unknown[];
}
interface SubmitReviewResponse {
  success: boolean;
  action: 'created' | 'updated';
  reviewId: string;
}

// Firestore rules deny all client writes to /reviews; all writes go through
// the reviews-submitReview callable which validates auth, role, dispute state,
// blind-period rules, edit window, and review window server-side.
export const submitReview = async (review: ReviewDocument) => {
  if (!review.reviewer_role) {
    throw new Error('reviewer_role is required to submit a review');
  }
  if (!review.subject_uid) {
    throw new Error('subject_uid is required to submit a review');
  }
  const payload: SubmitReviewPayload = {
    order_id: review.order_id,
    rating: review.rating,
    comment: review.comment,
    communication: review.communication as RatingChoice | undefined,
    delivery_time: review.delivery_time as RatingChoice | undefined,
    payment: review.payment as RatingChoice | undefined,
    reviewer_role: review.reviewer_role,
    subject_uid: review.subject_uid,
    products: review.products,
  };
  const call = httpsCallable<SubmitReviewPayload, SubmitReviewResponse>(
    functions,
    'reviews-submitReview'
  );
  const res = await call(payload);
  return res.data.reviewId;
};

export const getReviewsByUsername = async (username: string) => {
  const user = await getUserByUsername(username);
  if (!user) return [];
  const q = query(reviewsRef, where('uid', '==', user.uid));
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return [];
  }
  const data = snapshot.docs
    .map((doc) => reviewDocumentSchema.parse(doc.data()))
    .sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0)); // Sort newest first
  return data;
};

export const getReviewsByUid = async (uid: string) => {
  const q = query(reviewsRef, where('uid', '==', uid));
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return [];
  }
  const data = snapshot.docs
    .map((doc) => doc.data())
    .sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0)); // Sort newest first
  return data;
};

export interface StaticReview {
  id: string;
  img: string;
  name: string;
  content: string;
  googleReviewLink?: string;
}
export const STATIC_REVIEWS: StaticReview[] = [
  {
    id: '1',
    img: '/reviews/1.webp',
    name: 'Corey Hester',
    content:
      'First google review Ive ever been compelled to write. Gear Focus is awesome!! Great selection. Great website. INCREDIBLE customer service!! I had an issue with purchasing a product given my shipping was outside the continental US and had an issue with the checkout process and my credit card. However, Gear Focus and their team IMMEDIATELY got back to me after I reached out (despite it being past 5pm) and promptly fixed the issue with my checkout and shipping address in a professional and customer service friendly manner. I would 100% recommend this company, their products and services, and their team support to anyone. I will absolutely be purchasing from them again soon. 5-star.',
  },
  {
    id: '3',
    img: '/reviews/3.webp',
    name: 'Doug Newton',
    content:
      'I have had incredible service with GearFocus. All of the automated processes have functioned smoothly and the interaction is user-friendly without having to contact support. I have bought and sold (sold more than bought) about 8 items totaling over $15,000. On one occasion I needed their assistance to work through a problem with a customer. They were very personal, very supportive, and quick to help. You can tell they are experts in their field. They are by far my first choice in selling my equipment.',
  },
];

export const STATIC_REVIEWS_TWO: StaticReview[] = [
  {
    id: '1',
    img: 'https://lh3.googleusercontent.com/a-/AD5-WCkLUfgOBf0FkeaRNx5bfjgUzpjEujH6q0jgvXVb4w=w40-h40-p-rp-mo-br40',
    name: 'Jack Patmore',
    content:
      'Gear Focus is not only a great marketplace to use for creators to share their unused gear with others, but also a great community to be a part of on YouTube. I have sold multiple things through Gear Focus and their process is easy and simple!',
  },
  {
    id: '2',
    img: 'https://lh3.googleusercontent.com/a/AEdFTp6DIlmHPWmfm251cU-j2Jft5xMvpO-m1duEA_6F=w40-h40-p-rp-mo-br40',
    name: 'Paul Hancock',
    content:
      'I’ve been using Gear Focus for a good while now and it is definitely the place to go for good-quality photographic equipment.',
  },
  {
    id: '3',
    img: 'https://lh3.googleusercontent.com/a-/AD5-WCleVlpab7QEs3XKJ0BSW5zjIvWWjZIPVu4UN1rM=w40-h40-p-rp-mo-br40',
    name: 'Jason Drake',
    content:
      'Had a great experience working with the team at Gear Focus. They were friendly, responsive, and helpful. And the website was easy to use. We got a great used camera and got it quickly to allow us to start using it right away. Really happy with everything!',
  },
];

export interface RollingReview {
  id: string;
  source: 'Google' | 'Trustpilot';
  quote: string;
  name: string;
}

/**
 * Curated one-liners for the homepage's rolling review animation. Selected
 * by the CPO (2026-08-21) from the verified Google Business Profile Takeout
 * export (99 reviews) and the Trustpilot pull (40 reviews) — see
 * docs/c-level/shared/GOOGLE-REVIEWS-QUOTE-BANK-2026-08-08.md and
 * CUSTOMER-REVIEWS-2026-08-08.md. 5-star only, no competitor names (brand
 * hard rule), 6 buyer-side / 4 seller-side. Quotes verbatim except two
 * Bill-approved trims: Clint's missing apostrophe corrected, Joe's "I can
 * say that" lead-in dropped. Aaron & Brittany K.'s review predates the
 * 2024-11-01 acquisition (pulled forward per Bill, 2026-08-21) — everyone
 * else is post-acquisition.
 */
export const ROLLING_REVIEWS: RollingReview[] = [
  {
    id: 'damon',
    source: 'Google',
    quote:
      'Highly recommended, especially for high-value transactions where trust and service matter.',
    name: 'Damon K.',
  },
  {
    id: 'jared',
    source: 'Google',
    quote:
      "A real person actually helped me through the listing process when I had questions. That doesn't happen today!",
    name: 'Jared M.',
  },
  {
    id: 'clint',
    source: 'Trustpilot',
    quote:
      "Gearfocus personally called me. I'm floored with their detailed attention to my transaction.",
    name: 'Clint',
  },
  {
    id: 'keith',
    source: 'Google',
    quote:
      "If you're a seller, the fees are very reasonable compared to other online platforms, which means more money in your pocket!",
    name: 'Keith F.',
  },
  {
    id: 'harald',
    source: 'Google',
    quote: 'Excellent site with quality products at fair prices.',
    name: 'Harald B.',
  },
  {
    id: 'ely',
    source: 'Google',
    quote:
      "I turn to GearFocus and I'm selling my high dollar cameras because I know that it's a safe process.",
    name: 'Ely R.',
  },
  {
    id: 'joe',
    source: 'Trustpilot',
    quote:
      "The support I received from Beth at GearFocus was the best support I've received in the last many decades.",
    name: 'Joe',
  },
  {
    id: 'bernie',
    source: 'Google',
    quote:
      'The platform is clean, easy to navigate, and clearly built with photographers and gear enthusiasts in mind.',
    name: 'Bernie C.',
  },
  {
    id: 'james',
    source: 'Google',
    quote:
      'Buying has been very easy and other sellers are photography enthusiasts and extremely helpful in the process.',
    name: 'James',
  },
  {
    id: 'aaron-brittany',
    source: 'Google',
    quote:
      'Bought a used Canon R and 24-105mm lens and the seller responded quickly to my questions and the order arrived quickly.',
    name: 'Aaron & Brittany K.',
  },
];
